import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CopyToClipboard,
  Header,
  Link,
  Modal,
  Popover,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Tabs,
} from "@amzn/awsui-components-react";
import { CodeView } from "@amzn/awsui-code-view";
import {
  ClassificationEntry,
  ShadowTestResultResponse,
  ShadowTestDiscrepancy,
  ShadowTestLine,
  ShadowTestMatchSet,
  Stage,
} from "../../lusca-search-api/generated-src";
import { COUNTRY_TO_MARKETPLACE } from "../../helpers/marketplaceUtils";
import { downloadShadowTestCSV } from "../../helpers/csvExportUtils";
import { createFlashUIMatcherLink } from "../../helpers/linkUtils";

interface ShadowTestResultModalProps {
  visible: boolean;
  onDismiss: () => void;
  result: ShadowTestResultResponse | null | undefined;
  isLoading: boolean;
  error: Error | null;
  onRefresh: () => void;
  cliCommand: string;
  classifications: Record<string, ClassificationEntry>;
  onTriggerTest: () => void;
  isTriggerLoading: boolean;
  triggerError: Error | null;
  canTrigger: boolean;
}

export default function ShadowTestResultModal({
  visible,
  onDismiss,
  result,
  isLoading,
  error,
  onRefresh,
  cliCommand,
  classifications,
  onTriggerTest,
  isTriggerLoading,
  triggerError,
  canTrigger,
}: ShadowTestResultModalProps) {
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [triggerFailed, setTriggerFailed] = useState(false);
  const wasTriggerLoadingRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setTriggerSuccess(false);
      setTriggerFailed(false);
      wasTriggerLoadingRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    setTriggerSuccess(false);
    setTriggerFailed(false);
  }, [result]);

  useEffect(() => {
    if (isTriggerLoading) {
      wasTriggerLoadingRef.current = true;
      setTriggerSuccess(false);
      setTriggerFailed(false);
    } else if (wasTriggerLoadingRef.current) {
      wasTriggerLoadingRef.current = false;
      if (triggerError) {
        setTriggerFailed(true);
      } else {
        setTriggerSuccess(true);
      }
    }
  }, [isTriggerLoading, triggerError]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      );
    }

    if (error) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l">
            <StatusIndicator type="error">
              {error.message || "An unexpected error occurred."}
            </StatusIndicator>
          </Box>
          <Box textAlign="center">
            <Button iconName="refresh" onClick={onRefresh}>Retry</Button>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    if (!result) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l" color="text-status-inactive">
            No shadow test results found for this transaction.
          </Box>
          <Box textAlign="center">
            <SpaceBetween size="xs">
              <Button
                variant="primary"
                onClick={onTriggerTest}
                loading={isTriggerLoading}
                disabled={!canTrigger}
              >
                Run Shadow Test
              </Button>
              {triggerSuccess && (
                <StatusIndicator type="success">Test triggered</StatusIndicator>
              )}
              {triggerFailed && (
                <StatusIndicator type="error">Test failed</StatusIndicator>
              )}
            </SpaceBetween>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    const testDate = result.testTimestamp
      ? new Date(result.testTimestamp).toLocaleString()
      : "";

    const tabs: { id: string; label: string; content: JSX.Element }[] = [];

    if (result.matchedLines && result.matchedLines.length > 0) {
      tabs.push({
        id: "matched",
        label: `Matched (${result.matchedLines.length})`,
        content: <LineTable lines={result.matchedLines} classifications={classifications} />,
      });
    }

    if (result.gammaUniqueLines && result.gammaUniqueLines.length > 0) {
      tabs.push({
        id: "gamma-unique",
        label: `Gamma Unique (${result.gammaUniqueLines.length})`,
        content: <LineTable lines={result.gammaUniqueLines} classifications={classifications} />,
      });
    }

    if (result.prodUniqueLines && result.prodUniqueLines.length > 0) {
      tabs.push({
        id: "prod-unique",
        label: `Prod Unique (${result.prodUniqueLines.length})`,
        content: <LineTable lines={result.prodUniqueLines} classifications={classifications} />,
      });
    }

    if (result.jemUniqueLines && result.jemUniqueLines.length > 0) {
      tabs.push({
        id: "jem-unique",
        label: `JEM Unique (${result.jemUniqueLines.length})`,
        content: <LineTable lines={result.jemUniqueLines} classifications={classifications} />,
      });
    }

    if (result.discrepancies && result.discrepancies.length > 0) {
      tabs.push({
        id: "discrepancies",
        label: `Discrepancies (${result.discrepancies.length})`,
        content: <DiscrepancyTable discrepancies={result.discrepancies} classifications={classifications} />,
      });
    }

    if (result.matchSets && result.matchSets.length > 0) {
      tabs.push({
        id: "match-sets",
        label: `Match Sets (${result.matchSets.length})`,
        content: <MatchSetTable matchSets={result.matchSets} countryCode={result.countryCode} classifications={classifications} />,
      });
    }

    const usecaseComparison: { label: string; gamma: string; prod: string; jem: string }[] = [
      {
        label: "Usecases",
        gamma: result.gammaUsecases?.join(", ") || "-",
        prod: result.prodUsecases?.join(", ") || "-",
        jem: result.jemUsecases?.join(", ") || "-",
      },
      {
        label: "Lines",
        gamma: String(result.totalGammaLines ?? 0),
        prod: String(result.totalProdLines ?? 0),
        jem: "-",
      },
    ];

    return (
      <SpaceBetween size="s">
        <SpaceBetween size="xs" direction="horizontal">
          <StatusIndicator type={result.passed ? "success" : "error"}>
            {result.passed ? "PASS" : "FAIL"}
          </StatusIndicator>
          <Box color="text-body-secondary">·</Box>
          <Box>GL {result.glProductGroup}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.countryCode}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.paymentProvider || "-"}</Box>
          {result.isPromo && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box color="text-status-warning">Promo</Box>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box color="text-body-secondary">Tested {testDate}</Box>
        </SpaceBetween>

        <Table
          items={usecaseComparison}
          columnDefinitions={[
            { id: "label", header: "", cell: (e) => <Box fontWeight="bold">{e.label}</Box>, width: 100 },
            { id: "gamma", header: "Gamma", cell: (e) => e.gamma },
            { id: "prod", header: "Prod", cell: (e) => e.prod },
            ...(result.jemUsecases && result.jemUsecases.length > 0
              ? [{ id: "jem", header: "JEM", cell: (e: typeof usecaseComparison[0]) => e.jem }]
              : []),
          ]}
          variant="embedded"
          wrapLines
        />

        {tabs.length > 0 && <Tabs tabs={tabs} />}

        <CliCommandSection cliCommand={cliCommand} />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="max"
      header={
        <Header
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button iconName="refresh" onClick={onRefresh} loading={isLoading}>
                Refresh
              </Button>
              {result && (
                <>
                  <Button
                    iconName="download"
                    onClick={() => downloadShadowTestCSV(result, classifications)}
                  >
                    Export CSV
                  </Button>
                  <Button
                    onClick={onTriggerTest}
                    loading={isTriggerLoading}
                    disabled={!canTrigger}
                  >
                    Re-run Test
                  </Button>
                  {triggerSuccess && (
                    <StatusIndicator type="success">Test triggered</StatusIndicator>
                  )}
                  {triggerFailed && (
                    <StatusIndicator type="error">Test failed</StatusIndicator>
                  )}
                </>
              )}
            </SpaceBetween>
          }
        >
          Shadow Test Result
        </Header>
      }
    >
      {renderContent()}
    </Modal>
  );
}

const ClassificationCell = ({
  classification,
  passed,
  classifications,
}: {
  classification?: string;
  passed?: boolean;
  classifications: Record<string, ClassificationEntry>;
}) => {
  const indicator = (
    <StatusIndicator type={passed ? "success" : "error"}>
      {classification || "-"}
    </StatusIndicator>
  );

  const desc = classification ? classifications[classification]?.description : undefined;
  if (!desc) return indicator;

  return (
    <Popover
      dismissButton={false}
      position="top"
      size="medium"
      triggerType="custom"
      content={<Box>{desc}</Box>}
    >
      <span style={{ cursor: "help", borderBottom: "1px dotted" }}>{indicator}</span>
    </Popover>
  );
};

const lineColumns = (classifications: Record<string, ClassificationEntry>) => [
  { id: "usecase", header: "Usecase", cell: (e: ShadowTestLine) => e.usecase || "-" },
  { id: "account", header: "Account Number", cell: (e: ShadowTestLine) => e.accountNumber || "-" },
  { id: "side", header: "Journal Side", cell: (e: ShadowTestLine) => e.journalSide || "-" },
  { id: "companyCode", header: "Company Code", cell: (e: ShadowTestLine) => e.companyCode || "-" },
  { id: "location", header: "Location", cell: (e: ShadowTestLine) => e.location || "-" },
  { id: "costCenter", header: "Cost Center", cell: (e: ShadowTestLine) => e.costCenter || "-" },
  { id: "productLine", header: "Product Line", cell: (e: ShadowTestLine) => e.productLine || "-" },
  { id: "channel", header: "Channel", cell: (e: ShadowTestLine) => e.channel || "-" },
  { id: "project", header: "Project", cell: (e: ShadowTestLine) => e.project || "-" },
  {
    id: "functionalAmount",
    header: "Functional Amount",
    cell: (e: ShadowTestLine) =>
      e.functionalAmount ? `${e.functionalAmount} ${e.currency || ""}` : "-",
  },
  {
    id: "localAmount",
    header: "Local Amount",
    cell: (e: ShadowTestLine) =>
      e.localAmount ? `${e.localAmount} ${e.currency || ""}` : "-",
  },
  {
    id: "description",
    header: "GL Line Description",
    cell: (e: ShadowTestLine) => e.glLineDescription || "-",
  },
  { id: "setOfBooks", header: "Set of Books", cell: (e: ShadowTestLine) => e.setOfBooks || "-" },
  { id: "matchKey", header: "Match Key", cell: (e: ShadowTestLine) => e.matchKey || "-" },
  { id: "env", header: "Environment", cell: (e: ShadowTestLine) => e.environment || "-" },
  {
    id: "classification",
    header: "Classification",
    cell: (e: ShadowTestLine) => (
      <ClassificationCell classification={e.classification} passed={e.passed} classifications={classifications} />
    ),
  },
];

const LineTable = ({ lines, classifications }: { lines: ShadowTestLine[]; classifications: Record<string, ClassificationEntry> }) => (
  <div style={{ fontSize: "12px" }}>
    <Table<ShadowTestLine>
      items={lines}
      columnDefinitions={lineColumns(classifications)}
      variant="embedded"
      wrapLines
    />
  </div>
);

const DiscrepancyTable = ({ discrepancies, classifications }: { discrepancies: ShadowTestDiscrepancy[]; classifications: Record<string, ClassificationEntry> }) => (
  <Table<ShadowTestDiscrepancy>
    items={discrepancies}
    columnDefinitions={[
      { id: "field", header: "Field", cell: (e) => e.field || "-" },
      { id: "usecase", header: "Usecase", cell: (e) => e.usecase || "-" },
      { id: "gammaValue", header: "Gamma", cell: (e) => e.gammaValue || "-" },
      { id: "prodValue", header: "Prod", cell: (e) => e.prodValue || "-" },
      { id: "jemValue", header: "JEM", cell: (e) => e.jemValue || "-" },
      { id: "account", header: "Account", cell: (e) => e.accountNumber || "-" },
      {
        id: "classification",
        header: "Classification",
        cell: (e) => <ClassificationCell classification={e.classification} passed={e.passed} classifications={classifications} />,
      },
    ]}
    variant="embedded"
  />
);

const MatchSetTable = ({ matchSets, countryCode, classifications }: { matchSets: ShadowTestMatchSet[]; countryCode?: string; classifications: Record<string, ClassificationEntry> }) => (
  <Table<ShadowTestMatchSet>
    items={matchSets}
    columnDefinitions={[
      { id: "env", header: "Env", cell: (e) => e.environment || "-" },
      {
        id: "matchKey",
        header: "Match Key",
        cell: (e) => {
          if (!e.matchKey || !e.accountNumber || !e.environment) return e.matchKey || "-";
          const mpId = COUNTRY_TO_MARKETPLACE[countryCode || ""] || "ATVPDKIKX0DER";
          const url = createFlashUIMatcherLink(mpId, e.matchKey, Stage.Gamma, e.accountNumber);
          return <Link href={url} external>{e.matchKey}</Link>;
        },
      },
      { id: "account", header: "Account", cell: (e) => e.accountNumber || "-" },
      {
        id: "amount",
        header: "Balance",
        cell: (e) => (e.amount ? `${e.amount} ${e.currency || ""}` : "-"),
      },
      {
        id: "closed",
        header: "Closed",
        cell: (e) => (
          <StatusIndicator type={e.closed ? "success" : "warning"}>
            {e.closed ? "Yes" : "No"}
          </StatusIndicator>
        ),
      },
      {
        id: "classification",
        header: "Classification",
        cell: (e) => <ClassificationCell classification={e.classification} passed={e.passed} classifications={classifications} />,
      },
    ]}
    variant="embedded"
  />
);

const CliCommandSection = ({ cliCommand }: { cliCommand: string }) => (
  <Box>
    <Box variant="h4">Run locally</Box>
    <CodeView
      content={cliCommand}
      actions={
        <CopyToClipboard
          copyButtonAriaLabel="Copy command"
          copyErrorText="Failed to copy"
          copySuccessText="Copied"
          textToCopy={cliCommand}
        />
      }
    />
  </Box>
);
